// ============================================================================
// COFRE DO UTILIZADOR — o único sítio autorizado a escrever saldos
// ============================================================================
//
// O PROBLEMA QUE ISTO RESOLVE
// ---------------------------
// Havia três caminhos diferentes a escrever `saldoBolas` / `saldoEuro` no
// documento de `utilizadores`, e todos eles conseguiam apagar o valor real:
//
//   1. O arranque da app (Feed) fazia `setDoc(..., { saldoBolas: 0 }, { merge: true })`
//      sempre que o `getDoc` inicial dizia que a ficha não existia. Basta um
//      `getDoc` a falhar (rede fraca, leitura vinda da cache antes de sincronizar,
//      regra do Firestore a negar por um instante) para o saldo real ir a zero.
//      `merge: true` NÃO protege campos: ele escreve mesmo os campos que nomeia.
//
//   2. As compras de presentes liam o saldo, calculavam `saldo - preço` em
//      JavaScript e gravavam o número absoluto. Se o valor mudasse entre a
//      leitura e a escrita (outra aba, a consola do Firebase, um presente
//      recebido no mesmo segundo), essa alteração era esmagada.
//
//   3. O saldo era lido UMA vez com `getDoc` e nunca mais. Alterar o campo na
//      consola do Firebase não aparecia no ecrã, e a escrita absoluta seguinte
//      repunha o valor velho que estava em memória.
//
// AS REGRAS QUE PASSAM A VALER
// ----------------------------
//   • Valores por omissão só no PRIMEIRO registo. `garantirFichaUtilizador`
//     corre dentro de uma transação: se a ficha já existir, os campos de saldo
//     nunca entram na escrita.
//   • Toda a movimentação de dinheiro usa `increment()`. O servidor soma; o
//     cliente nunca envia um total absoluto e por isso nunca apaga nada.
//   • O saldo lê-se com `onSnapshot` (tempo real). Mexer no valor na consola do
//     Firebase reflete-se no ecrã em menos de um segundo, sem recarregar.

import { doc, runTransaction, onSnapshot, updateDoc, increment } from 'firebase/firestore';
import { db } from './firebase';
import { decidirEscritaDaFicha, camposDoMovimento, paraNumero } from './saldoRegras';

// As regras puras (o que se pode e o que NUNCA se pode escrever) estão em
// `saldoRegras.js`, que não importa Firebase e por isso é testável em Node.
export { FOTO_PADRAO, paraNumero, decidirEscritaDaFicha, camposDoMovimento } from './saldoRegras';

// Valores de arranque de uma conta nova criada pelo ecrã de registo.
export const SALDO_INICIAL_BOLAS = 600000;

/**
 * Garante que existe uma ficha em `utilizadores/{uid}` SEM nunca tocar no
 * dinheiro de uma ficha que já exista.
 *
 * Corre dentro de `runTransaction`, por isso a decisão "existe / não existe" e a
 * escrita acontecem no mesmo instante lógico no servidor. Não há a janela entre
 * o `getDoc` e o `setDoc` que antes deixava um saldo real ser reposto a zero.
 *
 * @param {import('firebase/auth').User} user  utilizador autenticado
 * @param {object} opcoes
 * @param {number} opcoes.bolasIniciais  saldo a atribuir SE (e só se) a ficha for criada agora
 * @returns {Promise<{criada: boolean, dados: object|null}>}
 */
export async function garantirFichaUtilizador(user, opcoes = {}) {
  if (!user?.uid) return { criada: false, dados: null };

  const refFicha = doc(db, "utilizadores", user.uid);

  return runTransaction(db, async (tx) => {
    const snap = await tx.get(refFicha);
    const dadosAtuais = snap.exists() ? (snap.data() || {}) : null;
    const { acao, escrita } = decidirEscritaDaFicha(snap.exists(), dadosAtuais, user, opcoes);

    if (acao === 'criar') {
      const ficha = { ...escrita, criadoEm: new Date() };
      tx.set(refFicha, ficha);
      return { criada: true, dados: ficha };
    }

    if (acao === 'remendar') tx.update(refFicha, escrita);
    return { criada: false, dados: { ...dadosAtuais, ...escrita } };
  });
}

/**
 * Liga um escutador em tempo real ao saldo do utilizador.
 *
 * Substitui o `getDoc` de uma só leitura que existia em todos os ecrãs. O
 * callback recebe sempre números já saneados.
 *
 * @returns {() => void} função para desligar o escutador
 */
export function observarSaldo(uid, aoMudar) {
  if (!uid) return () => {};
  return onSnapshot(
    doc(db, "utilizadores", uid),
    (snap) => {
      if (!snap.exists()) return;
      const dados = snap.data() || {};
      aoMudar({
        bolas: paraNumero(dados.saldoBolas),
        dados,
      });
    },
    (erro) => console.error("Escutador de saldo interrompido:", erro),
  );
}

/**
 * Move moedas virtuais numa ficha usando `increment()`.
 *
 * O servidor é que faz a soma, por isso duas escritas em paralelo (o presente
 * que envias e o presente que recebes no mesmo segundo) somam-se em vez de uma
 * apagar a outra. Valores a zero não são enviados para não sujar a escrita.
 *
 * @param {string} uid
 * @param {{bolas?: number, comissaoBolas?: number}} movimento
 */
export async function moverSaldo(uid, movimento = {}) {
  if (!uid) return;


  const campos = camposDoMovimento(movimento);
  const chaves = Object.keys(campos);
  if (chaves.length === 0) return;

  const escrita = {};
  for (const chave of chaves) escrita[chave] = increment(campos[chave]);
  await updateDoc(doc(db, "utilizadores", uid), escrita);
}
